// Attestation badge and verification detail panel.
//
// Each AI message displays a lock/shield icon: green when the upstream
// proxy is running with a real Intel TDX quote, yellow when the local
// mock attestation provider is in use. Clicking the badge opens a panel
// with the parsed image hash, MR fields, RTMR registers, report data, and
// the raw base64-encoded quote (with copy-to-clipboard buttons).
//
// Quote bytes come from the platform_getAttestationQuote tool result and
// are parsed in-browser via the SDK's parseQuote/imageHash helpers.
//
// XSS posture: the only innerHTML used here is for the inline lock SVG and
// the static spinner placeholder, both of which are hardcoded literals
// (never derived from model output or attestation bytes). All attestation
// data is rendered via textContent.

import { parseQuote, imageHash } from '@alphatoncapital/shroud-sdk';
import type { AttestationData } from './types.js';
import { executeToolCall } from './tools.js';

const TEE_TYPE_TDX = 0x00000081;

/**
 * Fetch attestation data for the most recent assistant turn.
 *
 * Calls platform_getAttestationQuote via the same JSON-RPC tool path the
 * orchestrator uses. Returns null on error or malformed payload — callers
 * should render a degraded "attestation unavailable" state in that case.
 */
export async function fetchAttestationForMessage(): Promise<AttestationData | null> {
  const result = await executeToolCall('platform_getAttestationQuote', {});
  if (result.error || !result.content) return null;

  const data = result.content as { attestation_quote?: string; image_hash?: string };
  if (!data.attestation_quote || !data.image_hash) return null;

  try {
    const quoteBytes = base64ToBytes(data.attestation_quote);
    const quote = parseQuote(quoteBytes);
    const isMock = quote.teeType !== TEE_TYPE_TDX;
    return {
      quoteBase64: data.attestation_quote,
      imageHashHex: data.image_hash,
      isMock,
    };
  } catch {
    // Quote came back but did not parse as a known TDX or mock layout.
    // Treat as mock so the operator can see something is off rather than
    // hiding the failure behind a green badge.
    return {
      quoteBase64: data.attestation_quote,
      imageHashHex: data.image_hash,
      isMock: true,
    };
  }
}

/** Render an attestation badge and append it to a message element. */
export function renderAttestationBadge(
  messageEl: HTMLElement,
  attestation: AttestationData,
): void {
  const badge = document.createElement('button');
  badge.type = 'button';
  badge.className = attestation.isMock
    ? 'hchat__badge hchat__badge--mock'
    : 'hchat__badge hchat__badge--tdx';
  badge.title = attestation.isMock ? 'Mock attestation' : 'TEE Verified';
  badge.setAttribute('aria-label', badge.title);
  badge.innerHTML = LOCK_SVG;

  badge.addEventListener('click', (e) => {
    e.stopPropagation();
    showVerificationPanel(badge, attestation);
  });

  messageEl.appendChild(badge);
}

/** Render a transient placeholder badge while attestation is being fetched. */
export function renderLoadingBadge(messageEl: HTMLElement): HTMLElement {
  const badge = document.createElement('span');
  badge.className = 'hchat__badge hchat__badge--loading';
  badge.title = 'Verifying...';
  badge.setAttribute('aria-label', 'Verifying attestation');
  badge.innerHTML = SPINNER_SVG;
  messageEl.appendChild(badge);
  return badge;
}

/** Render an "attestation unavailable" badge (no panel on click). */
export function renderErrorBadge(messageEl: HTMLElement): void {
  const badge = document.createElement('button');
  badge.type = 'button';
  badge.className = 'hchat__badge hchat__badge--error';
  badge.title = 'Attestation unavailable';
  badge.setAttribute('aria-label', badge.title);
  badge.innerHTML = ALERT_SVG;
  messageEl.appendChild(badge);
}

function showVerificationPanel(
  badge: HTMLElement,
  attestation: AttestationData,
): void {
  // Only one verification panel at a time.
  const existing = document.querySelector('.hchat__verify');
  if (existing) existing.remove();

  const panel = document.createElement('div');
  panel.className = 'hchat__verify';
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-label', 'Attestation verification details');

  const header = document.createElement('div');
  header.className = 'hchat__verify-header';
  const title = document.createElement('span');
  title.className = 'hchat__panel-title';
  title.textContent = attestation.isMock ? 'Mock Attestation' : 'TEE Attestation';
  header.appendChild(title);

  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'hchat__verify-close';
  closeBtn.textContent = '×';
  closeBtn.setAttribute('aria-label', 'Close');
  closeBtn.addEventListener('click', () => panel.remove());
  header.appendChild(closeBtn);
  panel.appendChild(header);

  addVerifyField(panel, 'Image Hash', attestation.imageHashHex);

  try {
    const quoteBytes = base64ToBytes(attestation.quoteBase64);
    const quote = parseQuote(quoteBytes);

    // Recompute the image hash client-side. If it diverges from the
    // server-reported value, the operator can spot the mismatch in the panel.
    const computedHex = bytesToHex(imageHash(quote));
    addVerifyField(panel, 'Computed Hash', computedHex);

    addVerifyField(panel, 'MRTD', bytesToHex(quote.mrTd), true);
    if (quote.rtmr) {
      for (let i = 0; i < quote.rtmr.length; i++) {
        addVerifyField(panel, `RTMR[${i}]`, bytesToHex(quote.rtmr[i]), true);
      }
    }
    addVerifyField(panel, 'Report Data', bytesToHex(quote.reportData), true);
  } catch {
    addVerifyField(panel, 'Parse Error', 'Could not parse quote details');
  }

  const rawLabel = document.createElement('div');
  rawLabel.className = 'hchat__verify-label';
  rawLabel.textContent = 'Raw Quote (Base64)';
  panel.appendChild(rawLabel);

  const rawPre = document.createElement('pre');
  rawPre.className = 'hchat__verify-value hchat__verify-raw';
  rawPre.textContent = attestation.quoteBase64;
  panel.appendChild(rawPre);

  const copyQuoteBtn = document.createElement('button');
  copyQuoteBtn.type = 'button';
  copyQuoteBtn.className = 'hchat__verify-copy';
  copyQuoteBtn.textContent = 'Copy Quote';
  copyQuoteBtn.addEventListener('click', () => {
    void copyToClipboard(attestation.quoteBase64, copyQuoteBtn, 'Copy Quote');
  });
  panel.appendChild(copyQuoteBtn);

  const bubble = badge.closest('.hchat__bubble');
  if (bubble) {
    bubble.after(panel);
  } else {
    badge.after(panel);
  }

  const escHandler = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      panel.remove();
      document.removeEventListener('keydown', escHandler);
    }
  };
  document.addEventListener('keydown', escHandler);

  // Defer the outside-click handler by one tick so that the click that
  // opened the panel is not the same click that closes it.
  const outsideHandler = (e: MouseEvent) => {
    const target = e.target as Node;
    if (!panel.contains(target) && target !== badge && !badge.contains(target)) {
      panel.remove();
      document.removeEventListener('click', outsideHandler);
      document.removeEventListener('keydown', escHandler);
    }
  };
  setTimeout(() => document.addEventListener('click', outsideHandler), 0);
}

function addVerifyField(
  panel: HTMLElement,
  label: string,
  value: string,
  truncate = false,
): void {
  const row = document.createElement('div');
  row.className = 'hchat__verify-field';

  const labelEl = document.createElement('div');
  labelEl.className = 'hchat__verify-label';
  labelEl.textContent = label;
  row.appendChild(labelEl);

  const valueEl = document.createElement('div');
  valueEl.className = 'hchat__verify-value';
  valueEl.textContent =
    truncate && value.length > 40
      ? `${value.slice(0, 20)}…${value.slice(-20)}`
      : value;
  valueEl.title = value;
  row.appendChild(valueEl);

  const copyBtn = document.createElement('button');
  copyBtn.type = 'button';
  copyBtn.className = 'hchat__verify-copy';
  copyBtn.textContent = 'Copy';
  copyBtn.addEventListener('click', () => {
    void copyToClipboard(value, copyBtn, 'Copy');
  });
  row.appendChild(copyBtn);

  panel.appendChild(row);
}

async function copyToClipboard(
  value: string,
  btn: HTMLButtonElement,
  defaultLabel: string,
): Promise<void> {
  try {
    await navigator.clipboard.writeText(value);
    btn.textContent = 'Copied';
  } catch {
    btn.textContent = 'Failed';
  }
  setTimeout(() => {
    btn.textContent = defaultLabel;
  }, 1400);
}

function base64ToBytes(b64: string): Uint8Array {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function bytesToHex(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i++) {
    out += bytes[i].toString(16).padStart(2, '0');
  }
  return out;
}

// Inline SVGs are static literals; the badge never inserts model output
// or attestation bytes via innerHTML.
const LOCK_SVG =
  '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>';

const ALERT_SVG =
  '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>';

const SPINNER_SVG =
  '<span class="hchat__spinner" aria-hidden="true" style="width:12px;height:12px"></span>';
